import { useSubmitSupportRequestMutation } from '@/hooks';
import { Button, InputRow, Modal, Select, Textarea, useToast } from '@/ui';
import PropTypes from 'prop-types';
import { useState } from 'react';

// "Escribir a soporte" — the form that reaches a person.
//
// It posts to /support-requests, which delivers an email to the support mailbox
// with the sender as reply-to. It used to post to /feedback, which opens a card
// on the tracker: that destination is right for reporting a problem, which the
// team prioritises and does not answer, and wrong for a question, which is what
// this form is titled to collect. A message asking for help that lands as a
// backlog card gets no reply, and nothing on screen said so.
//
// The endpoint takes one field and bounds it between 10 and 2000 characters.
// The type stays in the form and travels inside the message: it tells the
// person reading what kind of message it is, and the endpoint has no column for
// it.

const MIN_LEN = 10;
const MAX_LEN = 2000;

const TIPOS = [
  { value: 'question', label: 'Consulta' },
  { value: 'suggestion', label: 'Sugerencia' },
  { value: 'bug', label: 'Problema' },
];

export function EscribirSoporteModal({ open, onClose }) {
  const toast = useToast();
  const [tipo, setTipo] = useState('question');
  const [mensaje, setMensaje] = useState('');
  const [touched, setTouched] = useState(false);
  const mutation = useSubmitSupportRequestMutation();

  // The server bounds the message and answers in English. Saying it here keeps
  // the refusal in the language the form is written in, and keeps a message the
  // server would reject from being sent at all.
  const trimmed = mensaje.trim();
  const mensajeError =
    touched && (trimmed.length < MIN_LEN || trimmed.length > MAX_LEN);
  const mensajeHint =
    trimmed.length > MAX_LEN
      ? `El mensaje tiene un máximo de ${MAX_LEN} caracteres.`
      : `Escribe al menos ${MIN_LEN} caracteres para que soporte pueda ayudarte.`;

  function handleClose() {
    if (mutation.isPending) return;
    setTipo('question');
    setMensaje('');
    setTouched(false);
    mutation.reset();
    onClose?.();
  }

  function handleSubmit(e) {
    e.preventDefault();
    setTouched(true);
    if (trimmed.length < MIN_LEN || trimmed.length > MAX_LEN) return;

    const toastId = toast({
      kind: 'loading',
      message: 'Enviando…',
      dismissLabel: 'en curso',
    });

    const tipoLabel = TIPOS.find((t) => t.value === tipo)?.label ?? 'Consulta';
    mutation.mutate(
      // The page is the one piece of context the reader cannot ask for later
      // without a round trip, so it travels with the message.
      `[${tipoLabel}] ${trimmed}\n\nEnviado desde ${window.location.href}`,
      {
        onSuccess: (response) => {
          // recepthor-api wraps the result in { data, success, timestamp }.
          if (response?.success === false) {
            toast.update(toastId, {
              kind: 'err',
              message: 'No se pudo enviar. Inténtalo de nuevo.',
              duration: 0,
            });
            return;
          }
          toast.update(toastId, {
            kind: 'success',
            message: 'Mensaje enviado. Soporte responde a tu correo.',
            duration: 4000,
          });
          handleClose();
        },
        onError: () => {
          toast.update(toastId, {
            kind: 'err',
            message:
              'No se pudo enviar. Inténtalo de nuevo o escríbenos por correo.',
            duration: 0,
          });
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={handleClose} size="default">
      <Modal.Header title="Escribir a soporte" onClose={handleClose} />
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <InputRow label="Tipo">
            <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </InputRow>
          <InputRow label="Mensaje">
            <Textarea
              rows={4}
              value={mensaje}
              error={mensajeError}
              onChange={(e) => setMensaje(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="¿En qué te podemos ayudar?"
            />
            {mensajeError && (
              <p className="ayuda-soporte-modal__err">{mensajeHint}</p>
            )}
          </InputRow>
          <p className="ayuda-soporte-modal__note">
            Adjuntamos la pantalla actual. Soporte responde a tu correo.
          </p>
        </Modal.Body>
        <Modal.Foot>
          <Button
            variant="secondary"
            type="button"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Enviando…' : 'Enviar'}
          </Button>
        </Modal.Foot>
      </form>
    </Modal>
  );
}

EscribirSoporteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EscribirSoporteModal;
